import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip, ConfigProvider } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { MarketOverviewTable } from "../../../styles/MarketOverview.styled";
import { getOneFormulaColumns } from "./tableConfigs";
import { OneFormulaTableProps } from "../types";
import ConfirmationModal from "../SaveAllConfirmationModal";

const OneFormulaTable: React.FC<OneFormulaTableProps> = ({
  apiOneFormulaData,
  editingOneFormulaData,
  isEditingOneFormula,
  handleInputChange,
  handleApplyDefaultToEditingRow,
  handleSaveDefaultValue,
  handleApplyDefaultToAll,
}) => {
  const id_client = useSelector((state: any) => state.auth?.id_client);
  const [isApplyToAllModalOpen, setIsApplyToAllModalOpen] = useState(false);

  const handleApplyToAllClick = () => {
    setIsApplyToAllModalOpen(true);
  };

  const handleConfirmApplyToAll = () => {
    setIsApplyToAllModalOpen(false);
    handleApplyDefaultToAll();
  };

  const handleCancelApplyToAll = () => {
    setIsApplyToAllModalOpen(false);
  };

  const renderTooltipCell = (
    title: string,
    description: string,
    color: string
  ): React.ReactNode => (
    <Tooltip
      title={
        <div className="flex flex-col px-1 py-[0.15rem] justify-center items-start gap-1">
          <div className="color-white text-[0.625rem] font-normal">{title}</div>
          {description.trim() && <div className="w-full h-px bg-white" />}
          {description.trim() && (
            <div className="color-white text-[0.625rem] font-normal">
              {description}
            </div>
          )}
        </div>
      }
    >
      <InfoCircleTwoTone twoToneColor={color} />
    </Tooltip>
  );

  return (
    <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)] mb-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <div className="text-base font-semibold text-[#000000d9] font-[Inter] leading-8">
            Aggregated ONE Formula table
          </div>
        </div>
      </div>

      {/* Save and Apply to All Confirmation Modal */}
      <ConfirmationModal
        open={isApplyToAllModalOpen}
        onCancel={handleCancelApplyToAll}
        onConfirm={handleConfirmApplyToAll}
        title="Apply to All Markets?"
        content={
          <>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              This will copy the current default values to{" "}
            </span>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-medium leading-5">
              all market rows
            </span>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              . You can revert by clicking Discard All.
            </span>
          </>
        }
        confirmButtonText="Apply to All"
      />

      <ConfigProvider
        theme={{
          token: {
            fontFamily: "Inter",
          },
          components: {
            Table: {
              headerBorderRadius: 12,
              cellPaddingBlock: 8,
              cellPaddingInline: 12,
            },
          },
        }}
      >
        <MarketOverviewTable
          columns={getOneFormulaColumns({
            renderTooltipCell,
            id_client,
            isEditingOneFormula,
            editingOneFormulaData,
            handleInputChange,
            handleApplyDefaultToEditingRow,
            handleSaveDefaultValue,
            handleApplyToAllClick,
          })}
          dataSource={
            apiOneFormulaData.length > 0 ? apiOneFormulaData : []
          }
          pagination={false}
          size="small"
          rowClassName="text-center"
          bordered={true}
          scroll={{ x: "max-content" }}
        />
        <p className="mt-2 text-[0.75rem] font-semibold">
          Each KPI below contributes to your ONE Performance Score based on its
          assigned weight.{" "}
        </p>
        <p className="text-[0.75rem]">
          Adjust the weights to reflect your priorities for each market - for
          example, emphasizing Availability or Content performance.{" "}
        </p>
        <p className="text-[0.75rem] font-semibold">
          The total must equal 100% per market.
        </p>
        <p className="text-[0.75rem]">
          Click Edit, adjust the values, then Apply to save your configuration.
        </p>
      </ConfigProvider>
    </div>
  );
};

export default OneFormulaTable;
